import type { ReactNode } from 'react'

import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

export interface AnchoredPopoverProps {
  open: boolean
  anchorRect: DOMRect | null
  onOpenChange?: (open: boolean) => void
  children: ReactNode
  ariaLabel?: string
  contentClassName?: string
}

export function AnchoredPopover({
  open,
  anchorRect,
  onOpenChange,
  children,
  ariaLabel = 'Anchored popover content',
  contentClassName,
}: AnchoredPopoverProps) {
  if (!open || !anchorRect || typeof window === 'undefined') {
    return null
  }

  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const viewportPadding = 16
  const approxWidth = 320
  const approxHeight = 200

  const canShowBottom =
    viewportHeight - anchorRect.bottom - viewportPadding >= approxHeight
  const canShowTop =
    anchorRect.top - viewportPadding >= approxHeight
  const canShowRight =
    viewportWidth - anchorRect.right - viewportPadding >= approxWidth
  const canShowLeft =
    anchorRect.left - viewportPadding >= approxWidth

  let side: 'left' | 'right' | 'top' | 'bottom' = 'bottom'

  if (canShowBottom) {
    side = 'bottom'
  } else if (canShowTop) {
    side = 'top'
  } else if (canShowRight) {
    side = 'right'
  } else if (canShowLeft) {
    side = 'left'
  } else {
    side = 'bottom'
  }

  let x = anchorRect.left
  let y = anchorRect.bottom

  if (side === 'top') {
    x = anchorRect.left
    y = anchorRect.top
  } else if (side === 'right') {
    x = anchorRect.right
    y = anchorRect.top
  } else if (side === 'left') {
    x = anchorRect.left
    y = anchorRect.top
  }

  const align: 'start' | 'center' =
    side === 'bottom' || side === 'top' ? 'start' : 'center'

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="sr-only"
          aria-label={ariaLabel}
        />
      </PopoverTrigger>
      <PopoverAnchor
        style={{
          position: 'fixed',
          left: x,
          top: y,
        }}
      />
      <PopoverContent
        side={side}
        align={align}
        className={contentClassName}
      >
        {children}
      </PopoverContent>
    </Popover>
  )
}

